export type VARCategory = 'Infrastructure' | 'Cultural Resource' | 'Natural Resource';

export type VARSubcategory =
  | 'Community' | 'Camp' | 'Cottage/Cabin' | 'Weather Station'
  | 'Communication Site' | 'Industrial Site' | 'Power Generation'
  | 'Airport/Airstrip' | 'Mine' | 'Bridge' | 'Highway/Road'
  | 'Railway' | 'Dam' | 'Pipeline'
  | 'Cemetery/Burial Ground' | 'Heritage Site' | 'Archaeological Site'
  | 'Sacred/Spiritual Site' | 'Culturally Modified Trees'
  | 'Habitat Area' | 'Watershed' | 'Wetland'
  | 'Species at Risk Area' | 'Research Site';

export const VAR_SUBCATEGORY_MAP: Record<VARSubcategory, VARCategory> = {
  'Community': 'Infrastructure',
  'Camp': 'Infrastructure',
  'Cottage/Cabin': 'Infrastructure',
  'Weather Station': 'Infrastructure',
  'Communication Site': 'Infrastructure',
  'Industrial Site': 'Infrastructure',
  'Power Generation': 'Infrastructure',
  'Airport/Airstrip': 'Infrastructure',
  'Mine': 'Infrastructure',
  'Bridge': 'Infrastructure',
  'Highway/Road': 'Infrastructure',
  'Railway': 'Infrastructure',
  'Dam': 'Infrastructure',
  'Pipeline': 'Infrastructure',
  'Cemetery/Burial Ground': 'Cultural Resource',
  'Heritage Site': 'Cultural Resource',
  'Archaeological Site': 'Cultural Resource',
  'Sacred/Spiritual Site': 'Cultural Resource',
  'Culturally Modified Trees': 'Cultural Resource',
  'Habitat Area': 'Natural Resource',
  'Watershed': 'Natural Resource',
  'Wetland': 'Natural Resource',
  'Species at Risk Area': 'Natural Resource',
  'Research Site': 'Natural Resource',
};

export type Occupancy = 'None' | 'Periodic' | 'Year Round' | 'Unknown';
export type VARStatus = 'Active' | 'Archived';
export type RedFlagStatus = 'Active' | 'Resolved' | 'Cancelled';

// ---------------------------------------------------------------------------
// VAR Region Code Map
// Source: EM2 varRouter.js, GeoServer intellifire:parks_2025, intellifire:fmd_regions_2024,
//         NWT ECC Protected Area Registry (gov.nt.ca/ecc), Wikipedia
// ---------------------------------------------------------------------------

export interface VARRegion {
  name: string;      // Official / Indigenous name
  label: string;     // Short display label
  code: string;      // 2-letter VAR region code
  type: string;      // Area classification
  status: string;    // Current designation status
  match: string[];   // All name variants for spatial lookup (GeoServer, EM2, abbreviations)
}

export const VAR_REGIONS: VARRegion[] = [
  // --- FMD Regions (from intellifire:fmd_regions_2024) ---
  {
    name: 'Beaufort Delta / Inuvik', label: 'Beaufort Delta', code: 'EV',
    type: 'FMD Region', status: 'Active',
    match: ['Beaufort Delta', 'Beaufort-Delta', 'Inuvik'],
  },
  {
    name: 'Sahtu', label: 'Sahtu', code: 'VQ',
    type: 'FMD Region', status: 'Active',
    match: ['Sahtu'],
  },
  {
    name: 'South Slave', label: 'South Slave', code: 'SM',
    type: 'FMD Region', status: 'Active',
    match: ['South Slave'],
  },
  {
    name: 'North Slave', label: 'North Slave', code: 'ZF',
    type: 'FMD Region', status: 'Active',
    match: ['North Slave'],
  },
  {
    name: 'Deh Cho', label: 'Deh Cho', code: 'FS',
    type: 'FMD Region', status: 'Active',
    match: ['Deh Cho', 'Dehcho'],
  },

  // --- National Parks (from intellifire:parks_2025) ---
  {
    name: 'Wood Buffalo National Park', label: 'Wood Buffalo NP', code: 'WB',
    type: 'National Park', status: 'Established',
    match: ['Wood Buffalo', 'Wood Buffalo National Park', 'WBNP'],
  },
  {
    name: 'Aulavik National Park', label: 'Aulavik NP', code: 'AU',
    type: 'National Park', status: 'Established',
    match: ['Aulavik'],
  },
  {
    name: 'Tuktut Nogait National Park', label: 'Tuktut Nogait NP', code: 'TU',
    type: 'National Park', status: 'Established',
    match: ['Tuktut Nogait'],
  },

  // --- National Park Reserves (from intellifire:parks_2025) ---
  {
    name: 'Nahanni National Park Reserve', label: 'Nahanni NPR', code: 'NN',
    type: 'National Park Reserve', status: 'Established',
    match: ['Nahanni', 'Nahanni National Park', 'NNPR'],
  },
  {
    name: "Nááts'ihch'oh National Park Reserve", label: "Naats'ihch'oh NPR", code: 'NA',
    type: 'National Park Reserve', status: 'Established',
    match: ["Naats'ihch'oh", "Naats'ihch'oh National Park Reserve", 'NAATS'],
  },
  {
    name: 'Thaidene Nëné National Park Reserve', label: 'Thaidene Nëné', code: 'TN',
    type: 'National Park Reserve and Territorial Protected Area', status: 'Established',
    match: [
      'Thaidene Nene',
      'Thaidene Nene National Park Reserve',
      'Thaidene NÃ«nÃ© Territorial Protected Area',
    ],
  },

  // --- Indigenous Protected and Conserved Areas (from intellifire:parks_2025 + NWT ECC) ---
  {
    name: 'Edéhzhíe Protected Area', label: 'Edéhzhíe', code: 'ED',
    type: 'Indigenous Protected Area and National Wildlife Area', status: 'Established',
    match: ['Edehzhie'],
  },
  {
    name: "Ts'udé Nilįné Tuyeta", label: 'Tuyeta', code: 'TY',
    type: 'Indigenous Protected Area and Territorial Protected Area', status: 'Established',
    match: ['Tuyeta'],
  },
  {
    name: 'Saoyú (Grizzly Bear Mountain)', label: 'Sahyoue', code: 'SA',
    type: 'National Historic Site and Indigenous Protected Area', status: 'Established',
    match: ['Sahyoue'],
  },
  {
    name: 'ʔehdacho (Scented Grass Hills)', label: 'Edacho', code: 'EC',
    type: 'National Historic Site and Indigenous Protected Area', status: 'Established',
    match: ['Edacho'],
  },
  {
    name: 'Ezǫdzìtì', label: 'Ezǫdzìtì', code: 'EZ',
    type: 'Indigenous Protected Area', status: 'Established',
    // GeoServer stores as double-encoded UTF-8 (mojibake)
    match: ['EzoÌ¨dzÃ¬tÃ¬'],
  },

  // --- Wildlife Sanctuaries (from intellifire:parks_2025) ---
  {
    name: 'Thelon Wildlife Sanctuary', label: 'Thelon', code: 'TH',
    type: 'Wildlife Sanctuary', status: 'Established',
    match: ['Thelon'],
  },
  {
    name: 'MacKenzie Bison Wildlife Sanctuary', label: 'MacKenzie Bison', code: 'MB',
    type: 'Wildlife Sanctuary', status: 'Established',
    match: ['MacKenzie Bison'],
  },

  // --- Territorial Parks (from intellifire:parks_2025) ---
  {
    name: "Gwich'in Territorial Park", label: "Gwich'in", code: 'GT',
    type: 'Territorial Park', status: 'Established',
    match: ['Gwichin'],
  },

  // --- Migratory Bird Sanctuaries (from intellifire:parks_2025) ---
  {
    name: 'Anderson River Delta Migratory Bird Sanctuary', label: 'Anderson River Delta', code: 'AD',
    type: 'Migratory Bird Sanctuary', status: 'Established',
    match: ['Anderson River Delta'],
  },

  // --- Candidate Protected Areas (from NWT ECC - not yet in GeoServer) ---
  {
    name: "Sambaa K'e", label: "Sambaa K'e", code: 'SK',
    type: 'Candidate Protected Area', status: 'Candidate',
    match: ["Sambaa K'e", 'Sambaa Ke'],
  },
  {
    name: "Dınàgà Wek'èhodì", label: "Dınàgà Wek'èhodì", code: 'DW',
    type: 'Candidate Protected Area', status: 'Candidate',
    match: ["Dinaga Wek'ehodi"],
  },
  {
    name: 'Ejié Túé Ndáde', label: 'Ejié Túé Ndáde', code: 'EN',
    type: 'Candidate Protected Area', status: 'Candidate',
    match: ['Ejie Tue Ndade'],
  },
  {
    name: "Ka'a'gee Tu", label: "Ka'a'gee Tu", code: 'KT',
    type: 'Candidate Protected Area', status: 'Candidate',
    match: ["Ka'a'gee Tu", 'Kaagee Tu'],
  },
  {
    name: 'Łue Túé Sųlái', label: 'Łue Túé Sųlái', code: 'LS',
    type: 'Candidate Protected Area', status: 'Candidate',
    match: ['Lue Tue Sulai'],
  },
];

export type RegionCode =
  | 'EV' | 'VQ' | 'SM' | 'ZF' | 'FS'
  | 'WB' | 'AU' | 'TU'
  | 'NN' | 'NA' | 'TN'
  | 'ED' | 'TY' | 'SA' | 'EC' | 'EZ'
  | 'TH' | 'MB' | 'GT' | 'AD'
  | 'SK' | 'DW' | 'EN' | 'KT' | 'LS';

// Derived lookup: built from VAR_REGIONS match arrays (single source of truth)
export const REGION_NAME_TO_CODE: Record<string, RegionCode> = {};
for (const region of VAR_REGIONS) {
  for (const name of region.match) {
    REGION_NAME_TO_CODE[name] = region.code as RegionCode;
  }
}

export interface AccessFlags {
  Road: boolean;
  AircraftFW: boolean;
  AircraftRW: boolean;
  BoatRiver: boolean;
}

export interface Verification {
  Date: string | null;
  Method: string;
  Notes: string;
  VerifiedBy: string;
}

export interface ContactInfo {
  name?: string;
  phone?: string;
  email?: string;
}

export interface SprinklerFlag {
  active: boolean;
  reason: string;
  name: string;
  sprinkerlocaldate: string;
  created_by?: string;
}

export interface VARPhoto {
  FileUrl: string;
  Description?: string | null;
  Cardinal?: string | null;
  Disrepair?: string | null;
  FuelMitigation?: string | null;
  FuelProximity?: string | null;
  ForestFuels?: string | null;
  Hazmat?: string | null;
}

export interface VAREntity {
  id: string;
  var_id: string;
  name: string;
  details: string | null;
  var_type: VARCategory;
  var_subtype: string | null;
  keywords: string[];
  region: string | null;
  region_name: string | null;
  location: GeoJSON.Point | null;
  occupancy: Occupancy | null;
  status: VARStatus;
  archive_reason: string | null;
  red_flag_active: boolean;
  red_flag_status: RedFlagStatus | null;
  red_flag_reason: string | null;
  red_flag_date: string | null;
  red_flag_created_by: string | null;
  sprinklerFlag: SprinklerFlag;
  access: AccessFlags;
  verification: Verification;
  contact: ContactInfo | null;
  photos: VARPhoto[];
  data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface CreateVARInput {
  name: string;
  details: string;
  var_type?: VARCategory;
  var_subtype: VARSubcategory;
  keywords?: string[];
  region?: string;
  region_name?: string;
  location?: { lon: number; lat: number };
  occupancy?: Occupancy;
  status?: VARStatus;
  access?: Partial<AccessFlags>;
  verification?: Partial<Verification>;
  contact?: ContactInfo;
}

export interface UpdateVARInput {
  name?: string;
  details?: string;
  var_type?: VARCategory;
  var_subtype?: string;
  keywords?: string[];
  region?: string;
  region_name?: string;
  location?: { lon: number; lat: number };
  occupancy?: Occupancy;
  status?: VARStatus;
  access?: Partial<AccessFlags>;
  verification?: Partial<Verification>;
  contact?: ContactInfo;
  red_flag_active?: boolean;
  red_flag_status?: RedFlagStatus;
  red_flag_reason?: string;
  red_flag_created_by?: string;
  red_flag_date?: string;
  sprinklerFlag?: SprinklerFlag;
}

export interface VARFilters {
  var_type?: VARCategory;
  status?: VARStatus;
  region?: string;
  occupancy?: Occupancy;
  red_flag_active?: boolean;
  sprinkler_flag_active?: boolean;
  verification?: boolean;
  search?: string;
  var_id?: string;
  name?: string;
  details?: string;
  var_subtype?: string;
  keywords?: string;
}

// Sort field validation moved to runtime via varSchema (see varService.getAll).
export type VARSortField = string;

export interface VARPaginationOptions {
  page?: number;
  limit?: number;
  sortBy?: VARSortField;
  sortOrder?: 'asc' | 'desc';
}

export interface VARPaginatedResult {
  data: VAREntity[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}
